import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  FormControl,
  FormLabel,
} from '@mui/material';
import useStore from 'src/store/cns';
import { useNavigate } from 'react-router-dom';
import { handleError } from 'src/utils/errors/common';
import { getDomainTree } from 'src/utils/logs/domain';
import { updateDomains } from 'src/utils/data/domains';
import { setOwner, setTTL } from 'src/utils/contracts/registry';

const Settings: React.FC<{
  owner: string;
  ttl: string;
  domain: string;
  selectDomain: (domain: Domain | undefined) => void;
}> = ({ owner, ttl, domain, selectDomain }) => {
  const setDomainTree = useStore(state => state.setDomainTree);

  const [ttlInput, setTtlInput] = useState<string>('');
  const [ownerInput, setOwnerInput] = useState<string>('');

  const navigate = useNavigate();

  const handleTTLUpdate = async () => {
    try {
      await setTTL(domain.split('.'), ttlInput);
    } catch (error) {
      handleError(error, 'Failed to update TTL');
    }
  };

  const handleOwnerUpdate = async () => {
    try {
      await setOwner(domain.split('.'), ownerInput);

      const tree = await getDomainTree();
      setDomainTree(tree);
      await updateDomains();

      selectDomain(undefined);
      navigate('/cns');
    } catch (error) {
      handleError(error, 'Failed to update owner');
    }
  };

  const inputs = [
    {
      label: 'TTL (in ms)',
      prop: ttl,
      inputValue: ttlInput,
      function: setTtlInput,
      onClick: handleTTLUpdate,
    },
    {
      label: 'Owner',
      prop: owner,
      inputValue: ownerInput,
      function: setOwnerInput,
      onClick: handleOwnerUpdate,
    },
  ];

  useEffect(() => {
    setOwnerInput(owner);
    setTtlInput(ttl);
  }, [owner, ttl]);

  return (
    <Container maxWidth="sm">
      <Box>
        {inputs.map(input => (
          <Grid
            key={input.label}
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
            direction="row"
            mb={5}
          >
            <Grid item xs={8}>
              <FormControl fullWidth>
                <FormLabel
                  sx={{
                    position: 'absolute',
                    transform: 'translateY(-24px)',
                    pointerEvents: 'none',
                  }}
                >
                  {input.label}
                </FormLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={input.inputValue}
                  placeholder={`Set ${input.label}`}
                  onChange={e => input.function(e.target.value)}
                  InputLabelProps={{
                    shrink: false,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      '&:hover fieldset': {
                        borderColor: 'grey.600',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'grey.600',
                      },
                    },
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4} display="flex" justifyContent="center">
              <Button
                variant="contained"
                fullWidth
                onClick={input.onClick}
                sx={{
                  color: 'text.primary',
                  backgroundColor: 'primary.main',
                  padding: '10px 16px',
                }}
              >
                {input.prop.length > 0 ? (
                  <Typography>Update</Typography>
                ) : (
                  <Typography>Set</Typography>
                )}
              </Button>
            </Grid>
          </Grid>
        ))}
      </Box>
    </Container>
  );
};

export default Settings;
